import React, { useState, useEffect } from "react";
import { createRoot } from "react-dom/client";
import Swal from "sweetalert2";

import DeviceService from "../../../services/DeviceService";
import ProfileService from "../../../services/ProfileService";

// ===================== MAIN COMPONENT =====================
const MantraDevice = () => {
  const [pricePerUnit, setPricePerUnit] = useState(null);
  const [balance, setBalance] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Fetch data on mount
  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      // Fetch device price
      const priceResult = await DeviceService.getDevicePrice(
        "MANTRA",
        quantity,
      );

      if (!priceResult.success) {
        Swal.fire("Oops!", priceResult.message || "Request failed!", "error");
        return;
      }

      const priceData = priceResult.data;

      if (priceData.status === "SUCCESS") {
        setPricePerUnit(priceData.data.price);
      } else {
        Swal.fire(
          "Oops!",
          priceData.message || "Failed to fetch price",
          "error",
        );
      }

      // Fetch balance
      const balanceResult = await ProfileService.fetchBalance();

      if (!balanceResult.success) {
        // Swal.fire("Oops!", balanceResult.message || "Request failed!", "error");
        return;
      }

      const balanceData = balanceResult.data;

      if (balanceData.status === "SUCCESS") {
        setBalance(balanceData.data.main_wallet);
      } else {
        // Swal.fire(
        //   "Oops!",
        //   balanceData.message || "Failed to fetch balance",
        //   "error",
        // );
      }
    } catch (error) {
      Swal.fire("Oops!", "Request Failed: " + (error.message || ""), "error");
    } finally {
      setLoading(false);
    }
  };

  const totalPayable = pricePerUnit ? pricePerUnit * quantity : 0;
  const hasSufficientBalance = balance !== null && totalPayable <= balance;

  const handleQuantityChange = (delta) => {
    const newQty = quantity + delta;
    if (newQty >= 1 && newQty <= 10) {
      setQuantity(newQty);
    }
  };

  const handleBuyNow = () => {
    if (!pricePerUnit) return;
    setShowConfirmModal(true);
  };

  const confirmOrder = async () => {
    if (!pricePerUnit) return;
    setPlacingOrder(true);

    try {
      const result = await DeviceService.orderDevice("MANTRA", quantity);

      if (!result.success) {
        Swal.fire("Oops!", result.message || "Order failed!", "error");
        return;
      }

      const responseData = result.data;

      if (responseData.status === "SUCCESS") {
        Swal.fire(
          "Success!",
          responseData.message ||
            "Order placed successfully! Device will be delivered in 2-5 working days.",
          "success",
        );

        // Refresh balance after order
        const balanceResult = await ProfileService.fetchBalance();

        if (!balanceResult.success) {
          // Swal.fire(
          //   "Oops!",
          //   balanceResult.message || "Failed to refresh balance!",
          //   "error",
          // );
          return;
        }

        const balanceData = balanceResult.data;

        if (balanceData.status === "SUCCESS") {
          setBalance(balanceData.data.main_wallet);
        }

        setShowConfirmModal(false);
        setQuantity(1);
      } else {
        // Swal.fire(
        //   "Oops!",
        //   responseData.message || "Something went wrong!",
        //   "error",
        // );
      }
    } catch (error) {
      Swal.fire("Oops!", "Request Failed: " + (error.message || ""), "error");
    } finally {
      setPlacingOrder(false);
    }
  };

  const addMoneyNavigation = () => {
    // For Flutter WebView: send message to Flutter
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({ type: "ADD_MONEY" }),
      );
    } else {
      // Fallback - navigate to wallet page
      window.location.href = "/wallet";
    }
  };

  if (loading) {
    return (
      <div className="mantra-device-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading device & wallet...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mantra-device-container">
      {/* Device Card */}
      <div className="device-card">
        <img
          src="/assets/images/mantra-device.jpg"
          alt="Mantra MFS110 L1 Fingerprint Scanner"
          className="device-img"
          //   onError={(e) => {
          //     e.currentTarget.src =
          //       "https://via.placeholder.com/200?text=Mantra+MFS110";
          //   }}
        />
        <h2 className="device-title">Mantra MFS110 L1</h2>
        <p className="device-subtitle">
          FBI & STQC Certified Fingerprint Scanner
        </p>
        <div className="price">
          ₹{pricePerUnit?.toLocaleString("en-IN")}{" "}
          <span className="price-small">(Inclusive of GST)</span>
        </div>

        <ul className="feature-list">
          <li>✅ 500 DPI, FBI Image Quality</li>
          <li>✅ L1 Certification for UIDAI/Aadhaar</li>
          <li>✅ USB with Type C connector available</li>
          <li>✅ Durable Scratch-resistant Glass</li>
          <li>✅ Support Windows, Linux & Android</li>
          <li>✅ 1 Year Manufacturer Warranty</li>
        </ul>
        <div className="warning-note">
          📦 Delivery in <strong>3-5 working days</strong> across India. Free
          shipping.
        </div>
      </div>

      {/* Wallet Balance & Add Money */}
      <div className="device-card">
        <div className="balance-row">
          <div>
            <div className="balance-label">Available Wallet Balance</div>
            <div className="balance-amount">
              ₹{balance?.toLocaleString("en-IN") ?? 0}
            </div>
          </div>
          <button
            className="btn-custom btn-outline-custom btn-add-custom"
            onClick={() => {
              window.location.href = "/wallet";
            }}
          >
            + Add Money
          </button>
        </div>
      </div>

      {/* Quantity & Price Calculation */}
      <div className="device-card">
        <div style={{ marginBottom: 20 }}>
          <div className="text-muted-custom" style={{ marginBottom: 8 }}>
            Select Quantity
          </div>
          <div className="quantity-selector">
            <button
              className="qty-btn"
              onClick={() => handleQuantityChange(-1)}
              disabled={quantity <= 1}
            >
              −
            </button>
            <span className="qty-value">{quantity}</span>
            <button
              className="qty-btn"
              onClick={() => handleQuantityChange(1)}
              disabled={quantity >= 10}
            >
              +
            </button>
          </div>
        </div>

        <div className="price-detail">
          <div className="flex-between">
            <span>Device Price (x{quantity})</span>
            <span>
              ₹
              {pricePerUnit
                ? (pricePerUnit * quantity).toLocaleString("en-IN")
                : 0}
            </span>
          </div>
          <div className="flex-between">
            <span>GST (18%)</span>
            <span>Inclusive</span>
          </div>
          <div
            className="flex-between"
            style={{
              fontWeight: 700,
              fontSize: 18,
              borderTop: "1px solid #e2e8f0",
              marginTop: 8,
              paddingTop: 8,
            }}
          >
            <span>Total Payable</span>
            <span style={{ color: "#1e3a8a" }}>
              ₹{totalPayable.toLocaleString("en-IN")}
            </span>
          </div>
        </div>
      </div>

      {/* Order Button */}
      <div
        className="device-card"
        style={{ background: "transparent", boxShadow: "none", padding: 0 }}
      >
        <button
          className="btn-custom btn-primary-custom"
          onClick={handleBuyNow}
          disabled={!hasSufficientBalance || !pricePerUnit}
        >
          {!hasSufficientBalance ? "Insufficient Balance" : "Buy Now"}
        </button>
        {!hasSufficientBalance && balance !== null && (
          <p className="insufficient-text text-muted-custom">
            Add money to your wallet to complete purchase
          </p>
        )}
      </div>

      {/* Order Confirmation Modal */}
      {showConfirmModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Order</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowConfirmModal(false)}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3 d-flex justify-content-between">
                  <span>Quantity:</span>
                  <strong>{quantity}</strong>
                </div>
                <div className="mb-3 d-flex justify-content-between">
                  <span>Payable Amount:</span>
                  <strong className="text-primary">
                    ₹{totalPayable.toLocaleString("en-IN")}
                  </strong>
                </div>
                <div className="mb-3 d-flex justify-content-between">
                  <span>Wallet Balance:</span>
                  <strong className="text-success">
                    ₹{balance?.toLocaleString("en-IN")}
                  </strong>
                </div>
                <div className="mb-3 d-flex justify-content-between">
                  <span>Balance after order:</span>
                  <strong>
                    ₹{(balance - totalPayable).toLocaleString("en-IN")}
                  </strong>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowConfirmModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={confirmOrder}
                  disabled={placingOrder}
                >
                  {placingOrder ? "Placing..." : "Confirm & Pay"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ===================== STYLES (at the bottom of page) =====================
const styles = `
  .mantra-device-container {
    font-family: system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    background: #f5f7fb;
    padding: 16px;
    padding-bottom: 40px;
    min-height: 100vh;
  }

  .mantra-device-container .device-card {
    background: white;
    border-radius: 24px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    padding: 20px;
    margin-bottom: 20px;
  }

  .mantra-device-container .device-img {
    width: 100%;
    max-width: 200px;
    margin: 0 auto 16px;
    display: block;
    object-fit: contain;
  }

  .mantra-device-container .device-title {
    font-size: 1.6rem;
    font-weight: 700;
    margin-bottom: 4px;
    text-align: center;
  }

  .mantra-device-container .device-subtitle {
    color: #334155;
    margin-bottom: 12px;
    text-align: center;
  }

  .mantra-device-container .price {
    font-size: 28px;
    font-weight: 800;
    color: #1e3a8a;
    text-align: center;
  }

  .mantra-device-container .price-small {
    font-size: 14px;
    font-weight: 400;
    color: #64748b;
  }

  .mantra-device-container .feature-list {
    list-style: none;
    margin-top: 12px;
  }

  .mantra-device-container .feature-list li {
    padding: 6px 0;
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
  }

  .mantra-device-container .warning-note {
    background: #fef9c3;
    border-radius: 16px;
    padding: 12px;
    font-size: 13px;
    margin-top: 12px;
  }

  .mantra-device-container .balance-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
  }

  .mantra-device-container .balance-label {
    color: #475569;
    font-size: 14px;
  }

  .mantra-device-container .balance-amount {
    font-size: 28px;
    font-weight: 800;
  }

  .mantra-device-container .btn-custom {
    width: 100%;
    padding: 14px;
    border-radius: 60px;
    font-weight: 600;
    font-size: 16px;
    border: none;
    cursor: pointer;
    transition: 0.2s;
    text-align: center;
  }

  .mantra-device-container .btn-primary-custom {
    background: #1e3a8a;
    color: white;
  }

  .mantra-device-container .btn-primary-custom:active {
    background: #1e40af;
    transform: scale(0.97);
  }

  .mantra-device-container .btn-primary-custom:disabled {
    opacity: 0.6;
    transform: none;
    cursor: not-allowed;
  }

  .mantra-device-container .btn-outline-custom {
    background: transparent;
    border: 1.5px solid #1e3a8a;
    color: #1e3a8a;
  }

  .mantra-device-container .btn-add-custom {
    background: #10b981;
    color: white;
    margin-top: 8px;
    width: auto;
    padding: 10px 20px;
  }

  .mantra-device-container .quantity-selector {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #f1f5f9;
    border-radius: 60px;
    padding: 6px;
  }

  .mantra-device-container .qty-btn {
    width: 44px;
    height: 44px;
    border-radius: 40px;
    background: white;
    border: none;
    font-size: 24px;
    font-weight: bold;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    cursor: pointer;
  }

  .mantra-device-container .qty-btn:active {
    transform: scale(0.95);
  }

  .mantra-device-container .qty-value {
    font-size: 20px;
    font-weight: 600;
  }

  .mantra-device-container .price-detail {
    border-top: 1px solid #e2e8f0;
    padding-top: 12px;
    margin-top: 12px;
  }

  .mantra-device-container .flex-between {
    display: flex;
    justify-content: space-between;
    margin: 8px 0;
  }

  .mantra-device-container .text-muted-custom {
    color: #475569;
    font-size: 14px;
  }

  .mantra-device-container .text-success-custom {
    color: #10b981;
    font-weight: 700;
  }

  .mantra-device-container .loading-spinner {
    text-align: center;
    padding: 60px 20px;
  }

  .mantra-device-container .spinner {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 3px solid #1e3a8a;
    border-top-color: transparent;
    margin: 0 auto 16px;
    animation: mantra-spin 0.8s linear infinite;
  }

  @keyframes mantra-spin {
    to {
      transform: rotate(360deg);
    }
  }

  .mantra-device-container .overlay-custom {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .mantra-device-container .modal-custom {
    background: white;
    width: 90%;
    max-width: 320px;
    border-radius: 32px;
    padding: 24px;
    text-align: center;
  }

  .mantra-device-container .modal-buttons {
    display: flex;
    gap: 12px;
    margin-top: 24px;
  }

  .mantra-device-container .insufficient-text {
    text-align: center;
    font-size: 13px;
    margin-top: 8px;
  }

  /* Override any Bootstrap conflicts */
  .mantra-device-container button {
    font-family: inherit;
  }

  .mantra-device-container .btn-custom:focus,
  .mantra-device-container .qty-btn:focus {
    outline: none;
    box-shadow: none;
  }
`;

// Inject styles only once
if (!document.getElementById("mantra-device-styles")) {
  const styleSheet = document.createElement("style");
  styleSheet.id = "mantra-device-styles";
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}

// ===================== RENDER =====================
// Only render if we're on the mantra-device page
const isMantraDevicePage = window.location.pathname === "/mantra-device";

if (isMantraDevicePage) {
  const rootElement = document.getElementById("root");
  if (rootElement) {
    const root = createRoot(rootElement);
    root.render(<MantraDevice />);
  } else {
    const newRoot = document.createElement("div");
    newRoot.id = "root";
    document.body.appendChild(newRoot);
    const root = createRoot(newRoot);
    root.render(<MantraDevice />);
  }
}

export default MantraDevice;
